import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, Row, Col, Label, Input, Button, Spinner, Badge } from "reactstrap";
import { toast } from "react-toastify";
import { createResaleInventoryApi, updateResaleInventoryApi, uploadResaleInventoryMediaApi } from "../../apiServices/resaleInventoryServices";
import { parseIndianNumber, formatIndianNumber } from "../../utils/indianNumberFormatter";

const PROPERTY_TYPES = [
  "Apartment",
  "Villa",
  "Plot",
  "Commercial",
  "Independent House",
  "Penthouse",
  "Studio Apartment",
  "Other",
];

const BHK_OPTIONS = ["1 BHK", "2 BHK", "3 BHK", "4 BHK", "5+ BHK", "Studio", "N/A"];

const SALE_STATUSES = [
  "Available",
  "Under Negotiation",
  "Token Received",
  "Sold",
  "On Hold",
];

const ResaleInventoryModal = ({ isOpen, toggle, editData, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [imageUploadMode, setImageUploadMode] = useState("file"); // "file" or "url"
  const [videoUploadMode, setVideoUploadMode] = useState("file"); // "file" or "url"
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const [tempImageUrl, setTempImageUrl] = useState("");
  const [tempVideoUrl, setTempVideoUrl] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    contact_number: "",
    email: "",
    sell_price: "",
    sale_status: "Available",
    type: "Apartment",
    bhk: "3 BHK",
    size_sqft: "",
    floor: "",
    facing: "",
    furnishing: "Unfurnished",
    address: "",
    city: "",
    sector: "",
    microMarket: "",
    googleMapLink: "",
    images: [],
    videos: [],
  });

  useEffect(() => {
    if (editData) {
      setFormData({
        name: editData.name || "",
        contact_number: editData.contact_number || "",
        email: editData.email || "",
        sell_price: editData.sell_price || "",
        sale_status: editData.sale_status || "Available",
        type: editData.property_details?.type || "Apartment",
        bhk: editData.property_details?.bhk || "3 BHK",
        size_sqft: editData.property_details?.size_sqft || "",
        floor: editData.property_details?.floor || "",
        facing: editData.property_details?.facing || "",
        furnishing: editData.property_details?.furnishing || "Unfurnished",
        address: editData.property_details?.location?.address || "",
        city: editData.property_details?.location?.city || "",
        sector: editData.property_details?.location?.sector || "",
        microMarket: editData.property_details?.location?.microMarket || "",
        googleMapLink: editData.property_details?.location?.googleMapLink || "",
        images: editData.media?.images || [],
        videos: editData.media?.videos || [],
      });
    } else {
      setFormData({
        name: "",
        contact_number: "",
        email: "",
        sell_price: "",
        sale_status: "Available",
        type: "Apartment",
        bhk: "3 BHK",
        size_sqft: "",
        floor: "",
        facing: "",
        furnishing: "Unfurnished",
        address: "",
        city: "",
        sector: "",
        microMarket: "",
        googleMapLink: "",
        images: [],
        videos: [],
      });
    }
  }, [editData, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageFilesChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    try {
      setUploadingImage(true);
      const newUrls = [];
      for (const file of files) {
        const res = await uploadResaleInventoryMediaApi(file);
        if (res?.data?.url) {
          newUrls.push(res.data.url);
        }
      }
      if (newUrls.length > 0) {
        setFormData((prev) => ({
          ...prev,
          images: [...prev.images, ...newUrls],
        }));
        toast.success(`Successfully uploaded ${newUrls.length} image(s) to Cloudinary!`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to upload image(s)");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const handleVideoFilesChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    try {
      setUploadingVideo(true);
      const newUrls = [];
      for (const file of files) {
        const res = await uploadResaleInventoryMediaApi(file);
        if (res?.data?.url) {
          newUrls.push(res.data.url);
        }
      }
      if (newUrls.length > 0) {
        setFormData((prev) => ({
          ...prev,
          videos: [...prev.videos, ...newUrls],
        }));
        toast.success(`Successfully uploaded ${newUrls.length} video(s) to Cloudinary!`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to upload video(s)");
    } finally {
      setUploadingVideo(false);
      e.target.value = "";
    }
  };

  const handleAddImageUrl = () => {
    if (!tempImageUrl.trim()) return;
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, tempImageUrl.trim()],
    }));
    setTempImageUrl("");
  };

  const handleRemoveImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleAddVideoUrl = () => {
    if (!tempVideoUrl.trim()) return;
    setFormData((prev) => ({
      ...prev,
      videos: [...prev.videos, tempVideoUrl.trim()],
    }));
    setTempVideoUrl("");
  };

  const handleRemoveVideo = (index) => {
    setFormData((prev) => ({
      ...prev,
      videos: prev.videos.filter((_, i) => i !== index),
    }));
  };

  const validateAndFormatContactNumber = (inputNumber) => {
    if (!inputNumber) return { isValid: false, formatted: "", message: "Contact number is required" };

    let cleaned = String(inputNumber).trim().replace(/[\s\-\(\)]/g, "");

    if (cleaned.startsWith("+91")) {
      cleaned = cleaned.slice(3);
    } else if (cleaned.startsWith("91") && cleaned.length === 12 && /^[6-9]/.test(cleaned.slice(2))) {
      cleaned = cleaned.slice(2);
    } else if (cleaned.startsWith("0") && cleaned.length === 11 && /^[6-9]/.test(cleaned.slice(1))) {
      cleaned = cleaned.slice(1);
    }

    if (/^[6-9]\d{9}$/.test(cleaned)) {
      return {
        isValid: true,
        isIndian: true,
        formatted: cleaned,
      };
    }

    const rawWithPlus = cleaned.startsWith("+") ? cleaned : `+${cleaned}`;
    if (/^\+[1-9]\d{6,14}$/.test(rawWithPlus)) {
      return {
        isValid: true,
        isIndian: false,
        formatted: rawWithPlus,
      };
    }

    return {
      isValid: false,
      formatted: inputNumber,
      message: "Invalid contact number. Enter 10-digit Indian mobile number (e.g. 9876543210) or International number with country code (e.g. +14155552671)",
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.contact_number || !formData.sell_price || !formData.type) {
      toast.error("Please fill in all required fields (Name, Contact Number, Sell Price, Property Type)");
      return;
    }

    const phoneValidation = validateAndFormatContactNumber(formData.contact_number);
    if (!phoneValidation.isValid) {
      toast.error(phoneValidation.message);
      return;
    }

    const payload = {
      name: formData.name,
      contact_number: phoneValidation.formatted,
      email: formData.email,
      sell_price: parseIndianNumber(formData.sell_price),
      sale_status: formData.sale_status,
      property_details: {
        type: formData.type,
        bhk: formData.bhk,
        size_sqft: formData.size_sqft ? Number(formData.size_sqft) : undefined,
        floor: formData.floor,
        facing: formData.facing,
        furnishing: formData.furnishing,
        location: {
          address: formData.address,
          city: formData.city,
          sector: formData.sector,
          microMarket: formData.microMarket,
          googleMapLink: formData.googleMapLink,
        },
      },
      media: {
        images: formData.images,
        videos: formData.videos,
      },
    };

    try {
      setLoading(true);
      if (editData?._id) {
        await updateResaleInventoryApi(editData._id, payload);
        toast.success("Resale property inventory updated successfully!");
      } else {
        await createResaleInventoryApi(payload);
        toast.success("Resale property inventory created successfully!");
      }
      toggle();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save resale property");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered scrollable>
      <ModalHeader toggle={toggle}>
        {editData ? "Edit Resale Property Inventory" : "Add Resale Property Inventory"}
      </ModalHeader>
      <Form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <ModalBody style={{ maxHeight: "calc(80vh - 120px)", overflowY: "auto" }}>
          <h6 className="text-primary mb-3">Owner / Contact Information</h6>
          <Row>
            <Col md={6} className="mb-3">
              <Label className="form-label">
                Owner / Client Name <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g. Rahul Sharma"
                required
              />
            </Col>
            <Col md={6} className="mb-3">
              <Label className="form-label">
                Contact Number <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                name="contact_number"
                value={formData.contact_number}
                onChange={handleChange}
                placeholder="e.g. 9876543210 (Indian) or +14155552671 (Foreign)"
                required
              />
            </Col>
            <Col md={6} className="mb-3">
              <Label className="form-label">Email (Optional)</Label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="e.g. owner@example.com"
              />
            </Col>
            <Col md={6} className="mb-3">
              <Label className="form-label">
                Asking / Sell Price <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                name="sell_price"
                value={formData.sell_price}
                onChange={handleChange}
                placeholder="e.g. 1.35 Cr, 85 Lacs, or 7500000"
                required
              />
              {formData.sell_price && parseIndianNumber(formData.sell_price) > 0 && (
                <small className="text-success d-block mt-1 font-size-12 fw-semibold">
                  <i className="mdi mdi-check-circle me-1"></i>
                  Value: {formatIndianNumber(parseIndianNumber(formData.sell_price))} (₹ {parseIndianNumber(formData.sell_price).toLocaleString("en-IN")})
                </small>
              )}
            </Col>
          </Row>

          <h6 className="text-primary mt-3 mb-3">Property Details</h6>
          <Row>
            <Col md={4} className="mb-3">
              <Label className="form-label">
                Property Type <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                name="type"
                value={formData.type}
                onChange={handleChange}
              >
                {PROPERTY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={4} className="mb-3">
              <Label className="form-label">BHK Config</Label>
              <Input
                type="select"
                name="bhk"
                value={formData.bhk}
                onChange={handleChange}
              >
                {BHK_OPTIONS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={4} className="mb-3">
              <Label className="form-label">Size (Sq. Ft.)</Label>
              <Input
                type="number"
                name="size_sqft"
                value={formData.size_sqft}
                onChange={handleChange}
                placeholder="e.g. 1450"
              />
            </Col>
            <Col md={4} className="mb-3">
              <Label className="form-label">Floor</Label>
              <Input
                type="text"
                name="floor"
                value={formData.floor}
                onChange={handleChange}
                placeholder="e.g. 5th Floor / 14"
              />
            </Col>
            <Col md={4} className="mb-3">
              <Label className="form-label">Facing</Label>
              <Input
                type="text"
                name="facing"
                value={formData.facing}
                onChange={handleChange}
                placeholder="e.g. Park Facing / North-East"
              />
            </Col>
            <Col md={4} className="mb-3">
              <Label className="form-label">Furnishing Status</Label>
              <Input
                type="select"
                name="furnishing"
                value={formData.furnishing}
                onChange={handleChange}
              >
                <option value="Unfurnished">Unfurnished</option>
                <option value="Semi-Furnished">Semi-Furnished</option>
                <option value="Fully Furnished">Fully Furnished</option>
              </Input>
            </Col>
            <Col md={6} className="mb-3">
              <Label className="form-label">Sale Status</Label>
              <Input
                type="select"
                name="sale_status"
                value={formData.sale_status}
                onChange={handleChange}
              >
                {SALE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>

          <h6 className="text-primary mt-3 mb-3">Location & Address</h6>
          <Row>
            <Col md={6} className="mb-3">
              <Label className="form-label">Sector / Area</Label>
              <Input
                type="text"
                name="sector"
                value={formData.sector}
                onChange={handleChange}
                placeholder="e.g. Sector 150"
              />
            </Col>
            <Col md={6} className="mb-3">
              <Label className="form-label">City</Label>
              <Input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="e.g. Noida / Gurgaon"
              />
            </Col>
            <Col md={12} className="mb-3">
              <Label className="form-label">Full Address / Landmark</Label>
              <Input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="e.g. Flat 502, Tower A, Ace Parkway, Sector 150"
              />
            </Col>
            <Col md={12} className="mb-3">
              <Label className="form-label">Google Map Location Link</Label>
              <Input
                type="url"
                name="googleMapLink"
                value={formData.googleMapLink}
                onChange={handleChange}
                placeholder="https://maps.google.com/..."
              />
            </Col>
          </Row>

          <h6 className="text-primary mt-3 mb-3">Multiple Media Attachments (Images & Videos)</h6>
          <Row>
            {/* Multiple Images Upload & Link */}
            <Col md={6} className="mb-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <Label className="form-label mb-0 fw-semibold">
                  Property Photos ({formData.images.length})
                </Label>
                <div className="btn-group btn-group-sm">
                  <Button
                    type="button"
                    color={imageUploadMode === "file" ? "primary" : "soft-secondary"}
                    onClick={() => setImageUploadMode("file")}
                  >
                    <i className="mdi mdi-upload me-1"></i> Multi Upload
                  </Button>
                  <Button
                    type="button"
                    color={imageUploadMode === "url" ? "primary" : "soft-secondary"}
                    onClick={() => setImageUploadMode("url")}
                  >
                    <i className="mdi mdi-link-variant me-1"></i> Paste Link
                  </Button>
                </div>
              </div>

              {imageUploadMode === "file" ? (
                <div>
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageFilesChange}
                    disabled={uploadingImage}
                  />
                  {uploadingImage && (
                    <small className="text-primary d-block mt-1">
                      <Spinner size="sm" className="me-1" /> Uploading image(s) to Cloudinary...
                    </small>
                  )}
                </div>
              ) : (
                <div className="d-flex gap-2">
                  <Input
                    type="url"
                    value={tempImageUrl}
                    onChange={(e) => setTempImageUrl(e.target.value)}
                    placeholder="https://res.cloudinary.com/..."
                  />
                  <Button type="button" color="primary" onClick={handleAddImageUrl}>
                    Add
                  </Button>
                </div>
              )}

              {/* Visual Uploaded Images Preview Grid */}
              {formData.images.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mt-2 p-2 bg-light rounded" style={{ maxHeight: "150px", overflowY: "auto" }}>
                  {formData.images.map((img, i) => (
                    <div key={i} className="position-relative border rounded overflow-hidden bg-white shadow-sm" style={{ width: "85px", height: "75px" }}>
                      <img src={img} alt={`Preview ${i + 1}`} className="w-100 h-100" style={{ objectFit: "cover" }} />
                      <button
                        type="button"
                        className="btn btn-danger btn-sm p-0 position-absolute top-0 end-0 d-flex align-items-center justify-content-center"
                        style={{ width: "20px", height: "20px", borderRadius: "0 0 0 4px", fontSize: "12px", lineHeight: 1 }}
                        onClick={() => handleRemoveImage(i)}
                        title="Remove image"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Col>

            {/* Multiple Videos Upload & Link */}
            <Col md={6} className="mb-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <Label className="form-label mb-0 fw-semibold">
                  Property Videos ({formData.videos.length})
                </Label>
                <div className="btn-group btn-group-sm">
                  <Button
                    type="button"
                    color={videoUploadMode === "file" ? "primary" : "soft-secondary"}
                    onClick={() => setVideoUploadMode("file")}
                  >
                    <i className="mdi mdi-upload me-1"></i> Multi Upload
                  </Button>
                  <Button
                    type="button"
                    color={videoUploadMode === "url" ? "primary" : "soft-secondary"}
                    onClick={() => setVideoUploadMode("url")}
                  >
                    <i className="mdi mdi-link-variant me-1"></i> Paste Link
                  </Button>
                </div>
              </div>

              {videoUploadMode === "file" ? (
                <div>
                  <Input
                    type="file"
                    accept="video/*"
                    multiple
                    onChange={handleVideoFilesChange}
                    disabled={uploadingVideo}
                  />
                  {uploadingVideo && (
                    <small className="text-danger d-block mt-1">
                      <Spinner size="sm" className="me-1" /> Uploading video(s) to Cloudinary...
                    </small>
                  )}
                </div>
              ) : (
                <div className="d-flex gap-2">
                  <Input
                    type="url"
                    value={tempVideoUrl}
                    onChange={(e) => setTempVideoUrl(e.target.value)}
                    placeholder="https://res.cloudinary.com/..."
                  />
                  <Button type="button" color="primary" onClick={handleAddVideoUrl}>
                    Add
                  </Button>
                </div>
              )}

              {/* Visual Uploaded Videos Preview Grid */}
              {formData.videos.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mt-2 p-2 bg-light rounded" style={{ maxHeight: "150px", overflowY: "auto" }}>
                  {formData.videos.map((vid, i) => (
                    <div key={i} className="position-relative border rounded overflow-hidden bg-black shadow-sm" style={{ width: "110px", height: "75px" }}>
                      <video src={vid} className="w-100 h-100" style={{ objectFit: "cover" }} />
                      <button
                        type="button"
                        className="btn btn-danger btn-sm p-0 position-absolute top-0 end-0 d-flex align-items-center justify-content-center"
                        style={{ width: "20px", height: "20px", borderRadius: "0 0 0 4px", fontSize: "12px", lineHeight: 1 }}
                        onClick={() => handleRemoveVideo(i)}
                        title="Remove video"
                      >
                        ×
                      </button>
                      <div className="position-absolute bottom-0 start-0 end-0 bg-dark bg-opacity-75 text-white text-center font-size-10 py-1">
                        Video {i + 1}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="secondary" onClick={toggle} disabled={loading || uploadingImage || uploadingVideo}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={loading || uploadingImage || uploadingVideo}>
            {loading ? <Spinner size="sm" /> : editData ? "Update Inventory" : "Save Inventory"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default ResaleInventoryModal;
